import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { subscribeToPendingOvertimeRequests } from '@/services/queueService';
import { OvertimeRequestCard } from './OvertimeRequestCard';
import type { QueueTicket } from '@/types';

interface PendingOvertimeRequestsProps {
  branchId: string;
  barberId: string; // ✅ Now requires the correct barberId (document ID from barbers collection)
}

export function PendingOvertimeRequests({ branchId, barberId }: PendingOvertimeRequestsProps) {
  const { t } = useTranslation();
  const [pendingRequests, setPendingRequests] = useState<QueueTicket[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!barberId || !branchId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Timeout to prevent infinite loading if Firestore query hangs (missing index)
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 3000);

    const unsubscribe = subscribeToPendingOvertimeRequests(
      barberId, // ✅ Using correct barberId (document ID) instead of firebaseUser.uid
      branchId,
      (tickets) => {
        clearTimeout(loadingTimeout);
        setPendingRequests(tickets);
        setLoading(false);
      },
      (error) => {
        clearTimeout(loadingTimeout);
        console.error('Error subscribing to overtime requests:', error);
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
    };
  }, [barberId, branchId]);

  // Don't show anything if no pending requests
  if (!loading && pendingRequests.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="bg-warning/15 text-warning rounded-full p-2">
          <AlertTriangle className="w-5 h-5" aria-hidden="true" />
        </div>
        <h2 className="font-sans text-lg font-semibold text-ink">
          {t('barber.overtime.pendingRequests')}
        </h2>
        {pendingRequests.length > 0 && (
          <span className="bg-warning/15 text-warning border border-warning/30 text-xs font-bold px-2 py-1 rounded-full tabular-nums">
            {pendingRequests.length}
          </span>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-surface border border-hairline rounded-xl p-4 animate-pulse">
          <div className="h-4 bg-surface-3 rounded w-1/2 mb-2" />
          <div className="h-4 bg-surface-3 rounded w-3/4" />
        </div>
      )}

      {/* Request Cards */}
      {!loading && pendingRequests.length > 0 && (
        <div className="space-y-4">
          {pendingRequests.map((ticket) => (
            <OvertimeRequestCard
              key={ticket.queueId}
              ticket={ticket}
              onResponse={() => {
                // The subscription will automatically update the list
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
